import { preloadImage } from "@use/preload-image";

import { SliderModel } from "@courses-pm2/api/src/ts/models";
import { Engine, resolveMediaPath } from "@courses-pm2/api/src/ts/utils";

import { getChildrenByTagName } from "@courses-pm2/api/src/ts/parser/getChildrenByTagName";
import { getMetaValue } from "@courses-pm2/api/src/ts/parser/getMetaValue";
import { parseContentElement } from "@courses-pm2/api/src/ts/parser/parseContentElement";
import { parseCaptionedContentElement } from "../parseCaptionedContentElement";

/**
 * Helper that parses <Slider /> elements in the response from the pm2 backend.
 */
export async function parseSlider({
  node,
  portalId,
  engine,
}: {
  node: Element;
  portalId: string;
  engine?: Engine;
}): Promise<SliderModel> {
  // Announce that a model has been identified if possible
  if (engine) {
    engine.announce(SliderModel);
  }

  // Determine bullet type
  const bulletType = (() => {
    const bulletType = String(node.getAttribute("bulletType"));

    switch (bulletType.toLowerCase()) {
      case "prikker-med-nummer":
        return "numbered";

      case "prikker":
        return "dots";

      case "billedthumbs":
        return "thumb";

      default:
        console.warn(
          "parseSlider.extractBulletType(): " +
            `Unknown bullet type "${bulletType}", falling back to dots.`,
        );

        return "dots";
    }
  })();

  const displayType = (() => {
    switch (node.getAttribute("displayAs")) {
      case "reading-width":
        return "default";

      case "full-width":
        return "full-width";

      case "carousel":
        return "carousel";

      default:
        console.warn(
          "parseSlider.extractDisplayType(): " +
            `Unknown display type "${node.getAttribute(
              "displayAs",
            )}", falling back to default.`,
        );

        return "default";
    }
  })();

  const carouselCount = Number.parseInt(
    node.getAttribute("carruselCount") ?? "",
  );

  // Extract list of slides within this slider
  const slides = getChildrenByTagName(node, "SliderElement").map((slide) => ({
    caption: {
      title: slide.getAttribute("title") || undefined,
      description: slide.getAttribute("description") || undefined,
      authorOrArtist: slide.getAttribute("author") || undefined,
      copyright: slide.getAttribute("creditline") || undefined,
      year: slide.getAttribute("year") || undefined,
    },
    linkBackgroundColor: slide.getAttribute("linkBackground") || undefined,
    linkText: slide.getAttribute("linkText") || undefined,
    linkTextColor: slide.getAttribute("linkColor") || undefined,
    linkUrl: slide.getAttribute("linkUrl") || undefined,
    src: resolveMediaPath(slide.getAttribute("src")) || "",
    width: Number(slide.getAttribute("imageWidth") || "160"),
    height: Number(slide.getAttribute("imageHeight") || "90"),
  }));

  return new SliderModel({
    engine,
    portalId,

    // Extract basic configurations
    displayCaption: node.getAttribute("showCaptions") === "true",

    // Push bullet display config
    bulletType,
    displayType,
    carouselCount: Number.isNaN(carouselCount) ? undefined : carouselCount,

    // Push content
    slides,

    // Push basic configurations for a captioned content element
    ...parseCaptionedContentElement(node),
  });
}
